import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Play, BookOpen, Clock, Eye, Star, ArrowLeft, ExternalLink, CalendarDays } from 'lucide-react';
import { apiService } from '../services/api';
import analytics from '../services/analytics';
import { useAuth } from '../contexts/AuthContext';
import { useInitialData } from '../contexts/InitialDataContext';
import { useEnrollment } from '../hooks/useCourses';
import { RatingButtons } from '../components/RatingButtons';
import { SKILL_PAGE_CONTENT_LIMIT } from '../utils/skillContent';

function formatViews(num) {
  if (!num && num !== 0) return '0';
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (num >= 1_000) return `${Math.round(num / 1_000)}K`;
  return num.toLocaleString();
}

function SSRPlanSection({ plan }) {
  if (!plan || !plan.length) return null;
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
      <div className="bg-dark-card rounded-xl border border-white/[0.08] p-6">
        <h2 className="text-xl font-bold text-slate-100 mb-1">30-Day Learning Plan</h2>
        <p className="text-sm text-slate-400 mb-6">A structured day-by-day curriculum with curated resources</p>
        <div className="grid gap-3">
          {plan.map((day) => (
            <div
              key={day.day_number}
              className="flex items-start gap-4 px-4 py-3 rounded-lg bg-dark-surface/50 border border-white/[0.04]"
            >
              <span className="shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-teal/10 text-teal text-sm font-semibold">
                {day.day_number}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-200 break-words">
                  {day.title || `Day ${day.day_number}`}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-400">
                  {day.content_type && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/[0.06]">
                      {day.content_type === 'video' ? '▶' : '📄'} {day.content_type}
                    </span>
                  )}
                  {day.source && <span>{day.source}</span>}
                </div>
                {day.reason && (
                  <p className="text-xs text-slate-500 mt-1">{day.reason}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function SkillPage() {
  const { id: skillId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { plan: initialPlan, planSkillId } = useInitialData();
  const { isEnrolled, loading: enrollLoading, enroll, unenroll, enrollError } = useEnrollment(skillId);
  const [skillData, setSkillData] = useState(null);
  const [content, setContent] = useState({ videos: [], articles: [] });
  const [status, setStatus] = useState('loading'); // 'loading' | 'scraping' | 'pending' | 'ready' | 'error'
  const [activeTab, setActiveTab] = useState('videos');
  const [ratings, setRatings] = useState({ counts: {}, userRatings: {} });
  const [lastScrapedAt, setLastScrapedAt] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState(null);

  const initialTabSet = useRef(false);

  const skillViewedRef = useRef(false);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    analytics.contentTabSwitched(tab, skillId);
  };

  const fetchRatings = useCallback(async (videos, articles) => {
    const ids = [
      ...(videos || []).map(v => v.id),
      ...(articles || []).map(a => a.id),
    ].filter(Boolean);
    if (!ids.length) return { counts: {}, userRatings: {} };
    try {
      return await apiService.getRatings(ids);
    } catch {
      return { counts: {}, userRatings: {} };
    }
  }, []);

  const applyResult = useCallback((result, ratingsData = null) => {
    setSkillData(result.skill || null);
    if (result.skill && !skillViewedRef.current) {
      skillViewedRef.current = true;
      analytics.skillPageViewed(skillId, result.skill.name, {
        status: result.status,
        difficulty: result.skill.difficulty,
        category: result.skill.category,
        video_count: result.content?.videos?.length || 0,
        article_count: result.content?.articles?.length || 0,
      });
    }
    if (result.content) {
      setContent(result.content);
      if (result.content.lastScrapedAt) setLastScrapedAt(result.content.lastScrapedAt);
      // Auto-switch to articles tab if we have articles but no videos (first time only)
      if (!initialTabSet.current && result.content.articles?.length > 0 && !result.content.videos?.length) {
        setActiveTab('articles');
        initialTabSet.current = true;
      }
    }
    if (ratingsData) {
      setRatings(ratingsData);
    }
    setStatus(result.status || 'error');
  }, [skillId]);

  const loadSkillData = useCallback(async () => {
    setStatus('loading');
    try {
      const result = await apiService.getSkillContent(skillId);

      if (!result?.skill) {
        navigate('/', { replace: true });
        return;
      }

      if (result.skill.id && result.skill.id !== skillId) {
        navigate(`/skills/${result.skill.id}`, { replace: true });
        return;
      }

      // If content is ready, fetch ratings in parallel before rendering
      let ratingsData = { counts: {}, userRatings: {} };
      if (result.status === 'ready' && result.content) {
        ratingsData = await fetchRatings(result.content.videos, result.content.articles);
      }

      applyResult(result, ratingsData);

    } catch (error) {
      console.error('Error loading skill data:', error);
      setStatus('error');
    }
  }, [applyResult, fetchRatings, navigate, skillId]);

  useEffect(() => {
    skillViewedRef.current = false;
    loadSkillData();
  }, [skillId, loadSkillData]);

  const handleRefreshContent = async () => {
    setIsRefreshing(true);
    setRefreshMessage(null);
    analytics.track('skill_content_refresh_requested', { skill_id: skillId });
    try {
      const prevLastScraped = lastScrapedAt;
      const [result] = await Promise.all([
        apiService.getSkillContent(skillId),
        new Promise((resolve) => setTimeout(resolve, 1000)),
      ]);
      let ratingsData = { counts: {}, userRatings: {} };
      if (result.status === 'ready' && result.content) {
        ratingsData = await fetchRatings(result.content.videos, result.content.articles);
      }
      applyResult(result, ratingsData);
      if (result.status === 'scraping' || result.status === 'pending') {
        setRefreshMessage('Still gathering content — check again in a bit.');
        setTimeout(() => setRefreshMessage(null), 3000);
      } else if (result.content?.lastScrapedAt === prevLastScraped) {
        setRefreshMessage('already-up-to-date');
      } else {
        setRefreshMessage('Content updated!');
        setTimeout(() => setRefreshMessage(null), 3000);
      }
    } catch (error) {
      console.error('Error refreshing content:', error);
      setRefreshMessage('Refresh failed');
      setTimeout(() => setRefreshMessage(null), 3000);
    } finally {
      setIsRefreshing(false);
    }
  };

  // --- Loading skeleton ---
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-dark-bg p-8">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-dark-surface rounded w-1/3"></div>
            <div className="h-64 bg-dark-surface rounded"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-48 bg-dark-surface rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalVideos = content.videos?.length || 0;
  const totalArticles = content.articles?.length || 0;
  const displayedVideos = (content.videos || []).slice(0, SKILL_PAGE_CONTENT_LIMIT);
  const displayedArticles = (content.articles || []).slice(0, SKILL_PAGE_CONTENT_LIMIT);

  // Check if we have any content despite non-ready status
  const hasArticles = totalArticles > 0;
  const hasVideos = totalVideos > 0;
  const hasAnyContent = hasArticles || hasVideos;

  // --- Scraping in progress (only show if truly zero content) ---
  if ((status === 'scraping' || status === 'pending') && !hasAnyContent) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="text-6xl mb-6 animate-bounce">🔍</div>
          <h1 className="text-2xl font-bold text-slate-100 mb-3">
            Gathering the best resources for{' '}
            <span className="text-teal">{skillData?.name || skillId}</span>…
          </h1>
          <p className="text-slate-400 mb-6">
            We&apos;re gathering the initial resources for this skill. Give it a moment, then check again.
          </p>
          <div className="flex justify-center gap-3">
            <button onClick={loadSkillData} className="btn-primary">
              Check Again
            </button>
            <Link to="/" className="btn-secondary">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // --- Skill not found / error ---
  if (!skillData || status === 'error') {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-100 mb-4">Could not load skill</h1>
          <Link to="/" className="btn-primary">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  // --- Ready state ---
  return (
    <div className="min-h-screen bg-dark-bg">
      <Helmet>
        <title>{`Learn ${skillData.name} — Best Videos & Articles | LearnStack`}</title>
        <meta name="description" content={skillData.description || `Discover the best curated YouTube videos and articles to learn ${skillData.name}. Quality-ranked content so you get straight to learning.`} />
        <meta property="og:title" content={`Learn ${skillData.name} — Best Videos & Articles | LearnStack`} />
        <meta property="og:description" content={skillData.description || `Curated resources to learn ${skillData.name}.`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`Learn ${skillData.name} — Best Videos & Articles | LearnStack`} />
        <meta name="twitter:description" content={skillData.description || `Curated resources to learn ${skillData.name}.`} />
        <link rel="canonical" href={`${typeof window !== 'undefined' ? window.location.origin : ''}/skills/${skillData.id}`} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Course',
          name: skillData.name,
          description: skillData.description,
          provider: { '@type': 'Organization', name: 'LearnStack' },
          educationalLevel: skillData.difficulty,
          url: `https://learnstack.dev/skills/${skillData.id}`,
        })}</script>
      </Helmet>

      {/* Header */}
      <div className="bg-dark-card border-b border-white/[0.08]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link
            to="/"
            className="inline-flex items-center text-teal hover:text-teal-light mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Skills
          </Link>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-100 mb-2">{skillData.name}</h1>
              <p className="text-lg sm:text-xl text-slate-400 mb-4">{skillData.description}</p>
              <div className="flex flex-wrap items-center gap-3 text-sm text-slate-400">
                <span
                  className={`px-3 py-1 rounded-full ${
                    skillData.difficulty === 'beginner'
                      ? 'bg-green-500/15 text-green-400'
                      : skillData.difficulty === 'intermediate'
                      ? 'bg-yellow-500/15 text-yellow-400'
                      : 'bg-red-500/15 text-red-400'
                  }`}
                >
                  {skillData.difficulty?.charAt(0).toUpperCase() + skillData.difficulty?.slice(1)}
                </span>
                {skillData.estimatedHours > 0 && (
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>~{skillData.estimatedHours} hours</span>
                  </div>
                )}
                <span className="capitalize">{skillData.category}</span>
              </div>
            </div>

            <div className="flex flex-col items-start sm:items-end space-y-2 shrink-0">
              <div className="flex items-center space-x-3">
                {!user ? (
                  <Link to="/login" className="btn-secondary text-sm">
                    Sign in to enroll
                  </Link>
                ) : isEnrolled ? (
                  <button
                    onClick={async () => {
                      if (window.confirm('Unenroll from this course?')) await unenroll();
                    }}
                    disabled={enrollLoading}
                    className="flex items-center space-x-1 px-6 py-2 rounded-lg bg-green-500/15 text-green-400 font-medium text-sm hover:bg-green-500/25 transition-colors disabled:opacity-50"
                  >
                    <span>✓ Enrolled</span>
                  </button>
                ) : (
                  <div className="flex flex-col items-start gap-2">
                    <button
                      onClick={enroll}
                      disabled={enrollLoading}
                      className="btn-primary text-sm disabled:opacity-50"
                    >
                      ＋ Enroll in this Course
                    </button>
                    {enrollError && (
                      <p className="text-xs text-amber-400 max-w-xs">
                        {enrollError.code === 'FREE_PLAN_LIMIT_REACHED' ? (
                          <>
                            Free accounts support one active learning plan at a time.{' '}
                            <a href="/premium" className="underline hover:text-amber-300">Upgrade to Premium</a>{' '}
                            for unlimited simultaneous plans, or complete your current one first.
                          </>
                        ) : enrollError.message}
                      </p>
                    )}
                  </div>
                )}
                <button
                  onClick={handleRefreshContent}
                  disabled={isRefreshing || refreshMessage === 'already-up-to-date'}
                  className="btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed leading-[1.25rem]"
                >
                  <span className={isRefreshing ? 'animate-spin inline-block' : ''}>↻</span>{' '}
                  {isRefreshing ? 'Refreshing...' : refreshMessage === 'already-up-to-date' ? 'Up to date' : 'Refresh'}
                </button>
              </div>
              <span className="text-xs transition-all">
                {refreshMessage && refreshMessage !== 'already-up-to-date'
                  ? <span className="text-teal">{refreshMessage}</span>
                  : <span className="text-slate-500">{lastScrapedAt ? `Updated: ${new Date(lastScrapedAt).toLocaleDateString()}` : 'Never updated'}</span>
                }
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content Tabs */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-dark-card rounded-xl border border-white/[0.08]">
          {/* Tab Headers */}
          <div className="border-b border-white/[0.08]">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => handleTabChange('videos')}
                className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'videos'
                    ? 'border-teal text-teal'
                    : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Play className="w-4 h-4" />
                  <span>Videos ({Math.min(totalVideos, SKILL_PAGE_CONTENT_LIMIT)}{totalVideos > SKILL_PAGE_CONTENT_LIMIT ? ` of ${totalVideos}` : ''})</span>
                </div>
              </button>

              <button
                onClick={() => handleTabChange('articles')}
                className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'articles'
                    ? 'border-teal text-teal'
                    : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <BookOpen className="w-4 h-4" />
                  <span>Articles ({Math.min(totalArticles, SKILL_PAGE_CONTENT_LIMIT)}{totalArticles > SKILL_PAGE_CONTENT_LIMIT ? ` of ${totalArticles}` : ''})</span>
                </div>
              </button>

              <Link
                to={`/skills/${skillId}/plan`}
                className="py-4 text-sm font-medium border-b-2 border-transparent text-slate-400 hover:text-slate-200 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <CalendarDays className="w-4 h-4" />
                  <span>30-Day Plan</span>
                </div>
              </Link>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'videos' && (
              <div className="space-y-6">
                {/* Banner: videos coming soon when we have articles but no videos */}
                {!content.videos?.length && content.articles?.length > 0 && (
                  <div className="flex items-center gap-3 px-4 py-3 bg-teal/10 border border-teal/20 rounded-lg text-sm text-teal-light">
                    <span className="text-lg">🎬</span>
                    <p>Video content for this skill is being gathered and will be available within 24 hours. Check out the <button onClick={() => handleTabChange('articles')} className="underline font-medium hover:text-teal">articles</button> in the meantime!</p>
                  </div>
                )}
                {displayedVideos.length > 0 ? (
                  <>
                    {totalVideos > SKILL_PAGE_CONTENT_LIMIT && (
                      <p className="text-sm text-slate-400">
                        Showing {SKILL_PAGE_CONTENT_LIMIT} of {totalVideos} videos.
                      </p>
                    )}
                    {displayedVideos.map((video) => (
                      <div key={video.id} className="content-card overflow-hidden">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4">
                          <img
                            src={video.thumbnail || 'https://via.placeholder.com/320x180'}
                            alt={video.title}
                            className="w-full sm:w-32 h-44 sm:h-20 object-cover rounded-lg flex-shrink-0 mb-3 sm:mb-0"
                          />
                          <div className="min-w-0 flex-grow">
                            <h3 className="text-base sm:text-lg font-semibold text-slate-100 mb-2 break-words">
                              {video.title}
                            </h3>
                            <p className="text-slate-400 text-sm mb-3 line-clamp-2">{video.description}</p>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                              <div className="flex flex-wrap items-center gap-3 text-sm text-slate-400">
                                <span>{video.channel || video.source}</span>
                                <div className="flex items-center space-x-1">
                                  <Clock className="w-3 h-3" />
                                  <span>{video.duration}</span>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <Eye className="w-3 h-3" />
                                  <span>{formatViews(video.views)} views</span>
                                </div>
                                {video.rating && (
                                  <div className="flex items-center space-x-1">
                                    <Star className="w-3 h-3" />
                                    <span>{video.rating}/5</span>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center space-x-3">
                                <RatingButtons
                                  contentId={video.id}
                                  skillId={skillId}
                                  initialCounts={ratings.counts[video.id]}
                                  initialUserRating={ratings.userRatings[video.id]}
                                />
                                <a
                                  href={video.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn-primary flex items-center space-x-2"
                                  onClick={() => analytics.contentLinkClicked(skillId, video.id, 'video', video.url)}
                                >
                                  <Play className="w-4 h-4" />
                                  <span>Watch</span>
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="text-center py-12">
                    <Play className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-100 mb-2">No videos yet</h3>
                    <p className="text-slate-400 mb-4">
                      Content is being gathered. Try refreshing in a few minutes.
                    </p>
                    <button onClick={handleRefreshContent} className="btn-primary">
                      Find Videos
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'articles' && (
              <div className="space-y-6">
                {displayedArticles.length > 0 ? (
                  <>
                    {totalArticles > SKILL_PAGE_CONTENT_LIMIT && (
                      <p className="text-sm text-slate-400">
                        Showing {SKILL_PAGE_CONTENT_LIMIT} of {totalArticles} articles.
                      </p>
                    )}
                    {displayedArticles.map((article) => (
                      <div key={article.id} className="content-card overflow-hidden">
                        <h3 className="text-base sm:text-lg font-semibold text-slate-100 mb-2 break-words">
                          {article.title}
                        </h3>
                        <p className="text-slate-400 mb-4 text-sm sm:text-base line-clamp-3">{article.excerpt || article.description}</p>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-400">
                            <span>{article.source}</span>
                            <span>by {article.author}</span>
                            {article.readTime && (
                              <div className="flex items-center space-x-1">
                                <Clock className="w-3 h-3" />
                                <span>{article.readTime}</span>
                              </div>
                            )}
                            <span>{new Date(article.publishedDate).toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center space-x-3">
                            <RatingButtons
                              contentId={article.id}
                              skillId={skillId}
                              initialCounts={ratings.counts[article.id]}
                              initialUserRating={ratings.userRatings[article.id]}
                            />
                            <a
                              href={article.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-secondary flex items-center space-x-2"
                              onClick={() => analytics.contentLinkClicked(skillId, article.id, 'article', article.url)}
                            >
                              <BookOpen className="w-4 h-4" />
                              <span>Read</span>
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="text-center py-12">
                    <BookOpen className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-100 mb-2">No articles yet</h3>
                    <p className="text-slate-400 mb-4">
                      Content is being gathered. Try refreshing in a few minutes.
                    </p>
                    <button onClick={handleRefreshContent} className="btn-primary">
                      Find Articles
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* SSR-injected learning plan outline for SEO */}
      {planSkillId === skillId && <SSRPlanSection plan={initialPlan} />}
    </div>
  );
}
